import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, Eye, XCircle, FileText, UserCheck, UserX } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-800' },
  in_progress: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-800' },
  awaiting_documents: { label: 'Aguardando Docs', color: 'bg-yellow-100 text-yellow-800' },
  completed: { label: 'Concluído', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800' },
};

const TIPO_CONFIG = {
  admissao: { label: 'Admissão', icon: UserCheck },
  desligamento: { label: 'Desligamento', icon: UserX },
};

const RHProcessListPage = ({ tipoPadrao }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');

  useEffect(() => {
    loadProcessos();
  }, [tipoPadrao, filtroStatus]);

  const loadProcessos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (tipoPadrao) params.process_type_slug = tipoPadrao;
      if (filtroStatus) params.status = filtroStatus;
      const response = await axios.get(`${API}/api/rh/processos`, { params, headers: { Authorization: `Bearer ${token}` } });
      setProcessos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar processos');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Tem certeza que deseja cancelar este processo?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/rh/processos/${id}/cancelar`, { reason: 'Cancelado pelo usuário' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Processo cancelado');
      loadProcessos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {tipoPadrao === 'admissao' ? 'Admissões' : tipoPadrao === 'desligamento' ? 'Desligamentos' : 'Processos de RH'}
          </h1>
          <p className="text-sm text-gray-500">
            {tipoPadrao === 'admissao' ? 'Gerencie processos de admissão' : tipoPadrao === 'desligamento' ? 'Gerencie processos de desligamento' : 'Gerencie admissões e desligamentos'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filtroStatus || 'all'} onValueChange={(v) => setFiltroStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Todos os status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="draft">Rascunho</SelectItem>
              <SelectItem value="in_progress">Em Andamento</SelectItem>
              <SelectItem value="awaiting_documents">Aguardando Docs</SelectItem>
              <SelectItem value="completed">Concluído</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => navigate('/rh/processos/novo')}>
            <Plus className="h-4 w-4 mr-2" /> Novo Processo
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Regime</TableHead>
                <TableHead>Funcionário</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Carregando...</TableCell></TableRow>
              ) : processos.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Nenhum processo encontrado</TableCell></TableRow>
              ) : processos.map(p => {
                const TipoIcon = TIPO_CONFIG[p.process_type_slug]?.icon || FileText;
                const statusConf = STATUS_CONFIG[p.status] || { label: p.status, color: 'bg-gray-100 text-gray-800' };
                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TipoIcon className="h-4 w-4 text-gray-500" />
                        <span>{TIPO_CONFIG[p.process_type_slug]?.label || p.process_type_slug}</span>
                      </div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{p.contract_type?.toUpperCase()}</Badge></TableCell>
                    <TableCell>{p.employee_nome || '-'}</TableCell>
                    <TableCell><Badge className={statusConf.color}>{statusConf.label}</Badge></TableCell>
                    <TableCell>{formatDate(p.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/rh/processos/${p.id}`)} title="Visualizar">
                          <Eye className="h-4 w-4" />
                        </Button>
                        {p.status !== 'completed' && p.status !== 'cancelled' && (
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(p.id)} title="Cancelar">
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default RHProcessListPage;
